import { db } from '../components/Dexie';
import { log } from './loggingService';

const BASE_URL = 'https://deadapi.techysumo.it/techyshop';

/**
 * Aggiunge una nuova impostazione tramite API e localmente.
 * @param {string} chiave 
 * @param {string} valore 
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const addSetting = async (chiave, valore) => {
  try {
    log(`Aggiunta impostazione: ${chiave}=${valore}`, 'INFO');

    const response = await fetch(`${BASE_URL}/addImpostazioni`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ chiave, valore }),
    });

    const result = await response.json();

    if (result.result) {
      // Se l'API ha successo, aggiungiamo anche localmente
      await db.techyshop_impostazioni.add({
        chiave,
        valore
      });
      return { success: true, message: 'Impostazione aggiunta con successo!' };
    } else {
      throw new Error(result.text || "Errore durante l'aggiunta dell'impostazione.");
    }
  } catch (error) {
    log("Errore addSetting:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Elimina un'impostazione tramite API e localmente.
 * @param {number|string} id 
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteSetting = async (id) => {
  try {
    log(`Eliminazione impostazione ID: ${id}`, 'INFO');

    const response = await fetch(`${BASE_URL}/delImpostazioni`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ id }),
    });

    const result = await response.json();

    if (result.result) {
      // Se l'API ha successo, eliminiamo anche localmente
      await db.techyshop_impostazioni.delete(id);
      return { success: true, message: 'Impostazione eliminata con successo!' };
    } else {
      throw new Error(result.text || "Errore durante l'eliminazione dell'impostazione.");
    }
  } catch (error) {
    log("Errore deleteSetting:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Aggiorna un'impostazione tramite API e localmente.
 * @param {number|string} id 
 * @param {object} updates - Oggetto contenente i campi da aggiornare (es: { chiave: '...', valore: '...' })
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const updateSetting = async (id, updates) => {
  try {
    log(`Aggiornamento impostazione ID ${id}:`, 'INFO', updates);

    // Formattazione parametri come richiesto: array di oggetti { field, value }
    const values = Object.entries(updates).map(([field, value]) => ({ field, value }));
    const filters = [{ field: 'id', value: id }];

    const response = await fetch(`${BASE_URL}/editImpostazioni`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ values, filters }),
    });

    const result = await response.json();

    if (result.result) {
      // Se l'API ha successo, aggiorniamo anche localmente
      await db.techyshop_impostazioni.update(id, updates);
      return { success: true, message: 'Impostazione aggiornata con successo!' };
    } else {
      throw new Error(result.text || "Errore durante l'aggiornamento dell'impostazione.");
    }
  } catch (error) {
    log("Errore updateSetting:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};

/**
 * Sincronizza le impostazioni dal server a Dexie.
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const syncSettings = async () => {
  try {
    log("Sincronizzazione impostazioni in corso...", 'INFO');

    const response = await fetch(`${BASE_URL}/searchImpostazioni`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({}),
    });

    const result = await response.json();

    if (result.result && Array.isArray(result.details)) {
      await db.techyshop_impostazioni.clear();
      // Normalizziamo l'ID se necessario
      const normalized = result.details.map(i => ({ ...i, id: parseInt(i.id) }));
      await db.techyshop_impostazioni.bulkAdd(normalized);
      log(`Sincronizzate ${normalized.length} impostazioni.`, 'INFO');
      return { success: true, message: 'Impostazioni sincronizzate!' };
    } else {
      throw new Error(result.text || "Errore durante la sincronizzazione delle impostazioni.");
    }
  } catch (error) {
    log("Errore syncSettings:", 'ERROR', error);
    return { success: false, message: `Errore: ${error.message}` };
  }
};
